import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, User, Shield, Wrench } from 'lucide-react';
import { users as usersData, centres } from '../../data/mockData';

function FormulaireUtilisateur({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    role: 'technicien',
    centreId: '',
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  // useEffect doit être appelé AVANT tout return conditionnel
  useEffect(() => {
    if (isEditing) {
      const existingUser = usersData.find(u => u.id === parseInt(id));
      if (existingUser) {
        setFormData({
          username: existingUser.username,
          password: '',
          confirmPassword: '',
          nom: existingUser.nom,
          prenom: existingUser.prenom,
          email: existingUser.email,
          telephone: existingUser.telephone || '',
          role: existingUser.role,
          centreId: existingUser.centreId || '',
        });
      }
    }
  }, [id, isEditing]);

  // Vérifier que l'utilisateur est admin (après les hooks)
  if (user.role !== 'admin') {
    return (
      <div className="page-container">
        <div className="alert alert-error">
          Vous n'avez pas accès à cette page.
        </div>
      </div>
    );
  }

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'L\'identifiant est requis';
    } else if (formData.username.length < 3) {
      newErrors.username = 'L\'identifiant doit contenir au moins 3 caractères';
    }

    if (!isEditing && !formData.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }

    if (!formData.prenom.trim()) {
      newErrors.prenom = 'Le prénom est requis';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'L\'email n\'est pas valide';
    }

    if (formData.role === 'technicien' && !formData.centreId) {
      newErrors.centreId = 'Le centre est requis pour un technicien';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Effacer l'erreur quand l'utilisateur modifie le champ
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const userData = {
      id: isEditing ? parseInt(id) : usersData.length + 1,
      username: formData.username,
      password: formData.password || (isEditing ? usersData.find(u => u.id === parseInt(id))?.password : ''),
      nom: formData.nom,
      prenom: formData.prenom,
      email: formData.email,
      telephone: formData.telephone,
      role: formData.role,
      centreId: formData.role === 'technicien' ? parseInt(formData.centreId) : null,
    };

    console.log('Utilisateur sauvegardé:', userData);
    setMessage({ type: 'success', text: isEditing ? 'Utilisateur modifié avec succès!' : 'Utilisateur créé avec succès!' });
    
    setTimeout(() => {
      navigate('/utilisateurs');
    }, 1500);
  };

  return (
    <div className="page-container">
      <Link to="/utilisateurs" className="btn-back">
        <ArrowLeft size={16} />
        Retour à la liste
      </Link>

      <div className="page-header">
        <div>
          <h1>{isEditing ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h1>
          <p className="text-muted">
            {isEditing ? 'Modifier les informations de l\'utilisateur' : 'Créer un nouveau compte utilisateur'}
          </p>
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit} className="form-card">
          <div className="form-section">
            <h2>Informations de connexion</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="username">Identifiant *</label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="Ex: tech.martin"
                  className={errors.username ? 'input-error' : ''}
                />
                {errors.username && <span className="error-text">{errors.username}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="email@brillanty.fr"
                  className={errors.email ? 'input-error' : ''}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="password">
                  {isEditing ? 'Nouveau mot de passe' : 'Mot de passe *'}
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder={isEditing ? 'Laisser vide pour ne pas modifier' : '••••••••'}
                  className={errors.password ? 'input-error' : ''}
                />
                {errors.password && <span className="error-text">{errors.password}</span>}
                {isEditing && <small>Laisser vide pour conserver le mot de passe actuel</small>}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirmer le mot de passe</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  className={errors.confirmPassword ? 'input-error' : ''}
                />
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>Informations personnelles</h2>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="prenom">Prénom *</label>
                <input
                  type="text"
                  id="prenom"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  placeholder="Prénom"
                  className={errors.prenom ? 'input-error' : ''}
                />
                {errors.prenom && <span className="error-text">{errors.prenom}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="nom">Nom *</label>
                <input
                  type="text"
                  id="nom"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  placeholder="Nom"
                  className={errors.nom ? 'input-error' : ''}
                />
                {errors.nom && <span className="error-text">{errors.nom}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="telephone">Téléphone</label>
              <input
                type="tel"
                id="telephone"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                placeholder="06 XX XX XX XX"
              />
            </div>
          </div>

          <div className="form-section">
            <h2>Rôle et permissions</h2>

            <div className="role-selector">
              <div 
                className={`role-option ${formData.role === 'admin' ? 'selected' : ''}`}
                onClick={() => handleChange({ target: { name: 'role', value: 'admin' } })}
              >
                <Shield size={32} className="role-icon" />
                <div className="role-info">
                  <h3>Administrateur</h3>
                  <p>Accès complet à l'ensemble des données de l'application</p>
                </div>
              </div>

              <div 
                className={`role-option ${formData.role === 'technicien' ? 'selected' : ''}`}
                onClick={() => handleChange({ target: { name: 'role', value: 'technicien' } })}
              >
                <Wrench size={32} className="role-icon" />
                <div className="role-info">
                  <h3>Technicien</h3>
                  <p>Accès aux interventions attribuées, stock des centres et pointage</p>
                </div>
              </div>
            </div>

            {formData.role === 'technicien' && (
              <div className="form-group">
                <label htmlFor="centreId">Centre de rattachement *</label>
                <select
                  id="centreId"
                  name="centreId"
                  value={formData.centreId}
                  onChange={handleChange}
                  className={errors.centreId ? 'input-error' : ''}
                >
                  <option value="">Sélectionnez un centre</option>
                  {centres.filter(c => c.actif).map(centre => (
                    <option key={centre.id} value={centre.id}>
                      {centre.nom} - {centre.ville}
                    </option>
                  ))}
                </select>
                {errors.centreId && <span className="error-text">{errors.centreId}</span>}
              </div>
            )}
          </div>

          <div className="form-actions">
            <Link to="/utilisateurs" className="btn btn-secondary">
              Annuler
            </Link>
            <button type="submit" className="btn btn-primary">
              <Save size={20} />
              {isEditing ? 'Enregistrer les modifications' : 'Créer l\'utilisateur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default FormulaireUtilisateur;

